import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function HomebuyerElectricalReportsPage() {
  useEffect(() => {
    // Set page title and meta description
    document.title = 'Homebuyer Electrical Reports Watford | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Pre-purchase electrical surveys in Watford. Homebuyer electrical reports & property inspections. NICEIC approved. Call 020 3198 1818.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Pre-purchase electrical surveys in Watford. Homebuyer electrical reports & property inspections. NICEIC approved. Call 020 3198 1818.';
      document.head.appendChild(meta);
    }

    // Add LocalBusiness JSON-LD schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical Services",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "url": "https://beyondelectrical.co.uk/services/safety-compliance/homebuyer-electrical-reports",
      "telephone": "+442031981818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "areaServed": {
        "@type": "City",
        "name": "Watford"
      },
      "serviceType": "Homebuyer Electrical Reports",
      "priceRange": "££",
      "openingHours": "Mo-Su 00:00-23:59"
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const areasServed = [
    'Watford Town Centre',
    'Oxhey',
    'Bushey',
    'Croxley Green',
    'Abbots Langley',
    'Rickmansworth',
    'Garston',
    'North Watford',
    'West Watford',
    'Carpenders Park',
    'Leavesden',
    'Nascot Wood',
    'Holywell',
    'Meriden',
    'Cassiobury'
  ];

  const whatsIncluded = [
    {
      icon: '🏡',
      title: 'Pre-Purchase Inspection',
      description: 'Comprehensive electrical survey before you commit to buying your new home'
    },
    {
      icon: '🔍',
      title: 'Hidden Issues Detection',
      description: 'Identify outdated wiring, safety hazards, and potential costly repairs'
    },
    {
      icon: '💰',
      title: 'Cost Estimates',
      description: 'Detailed breakdown of any remedial work needed with approximate costs'
    },
    {
      icon: '📊',
      title: 'Professional Report',
      description: 'Clear, easy-to-understand report with photos and recommendations'
    },
    {
      icon: '⚡',
      title: 'Safety Assessment',
      description: 'Full evaluation of consumer unit, earthing, bonding, and circuit protection'
    },
    {
      icon: '🔧',
      title: 'Negotiation Tool',
      description: 'Use findings to negotiate price or request repairs before completion'
    }
  ];

  const benefits = [
    {
      icon: '🛡️',
      title: 'Peace of Mind',
      description: 'Know exactly what electrical condition your new home is in before buying'
    },
    {
      icon: '💸',
      title: 'Save Money',
      description: 'Avoid unexpected electrical repair bills after moving in'
    },
    {
      icon: '📋',
      title: 'Mortgage Ready',
      description: 'Some lenders require electrical reports for older properties'
    },
    {
      icon: '🏠',
      title: 'Investment Protection',
      description: 'Protect your biggest investment with professional electrical assessment'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628]"></div>
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://public.readdy.ai/ai/img_res/1ef5fe5bae623c8ffe034b74764fef26.jpg" 
            alt="Homebuyer Electrical Reports Background" 
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <span className="text-white text-sm font-medium">🏆 NICEIC Approved</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <span className="text-white text-sm font-medium">📍 Watford</span>
            </div>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Homebuyer Electrical Reports<br />
            <span className="text-[#D4AF37]">in Watford</span>
          </h1>
          
          <p className="text-xl text-gray-200 mb-8 max-w-2xl leading-relaxed">
            Professional pre-purchase electrical surveys for homebuyers. Identify hidden issues, estimate repair costs, and buy with confidence. NICEIC certified inspections.
          </p>
          
          <div className="flex flex-wrap gap-4">
            <a 
              href="tel:02031981818"
              className="bg-[#D4AF37] text-[#0A1628] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#C5A028] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-phone-line"></i>
              Call 020 3198 1818
            </a>
            <Link 
              to="/#contact"
              className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 border border-white/30 whitespace-nowrap"
            >
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">What's Included in Our Homebuyer Report</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive electrical assessment to help you make an informed purchase decision
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whatsIncluded.map((item, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#0A1628] to-[#1a2942] rounded-lg mb-6 text-3xl">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">Why Get a Homebuyer Electrical Report?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Essential due diligence for any property purchase
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="text-center p-6 rounded-xl hover:bg-gray-50 transition-all duration-300"
              >
                <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#D4AF37] to-[#C5A028] rounded-full mb-6 text-4xl mx-auto shadow-lg">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Choose Beyond Electrical?</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Watford's trusted NICEIC approved electricians for homebuyer surveys
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-shield-check-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">NICEIC Approved</h3>
              <p className="text-gray-300 text-sm">Professional certification you can trust</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-time-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Fast Turnaround</h3>
              <p className="text-gray-300 text-sm">Reports within 24-48 hours</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-file-list-3-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Clear Reports</h3>
              <p className="text-gray-300 text-sm">Easy-to-understand documentation</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-customer-service-2-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Expert Guidance</h3>
              <p className="text-gray-300 text-sm">Professional advice on findings</p>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 max-w-4xl mx-auto">
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 flex items-center justify-center bg-[#D4AF37] rounded-full text-4xl flex-shrink-0">
                🏆
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2">Homebuyer Survey Specialists</h3>
                <p className="text-gray-300 leading-relaxed">
                  Our NICEIC approved electricians have extensive experience in pre-purchase electrical surveys. We provide clear, honest assessments to help you make informed decisions about your property purchase.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">Areas We Cover</h2>
            <p className="text-xl text-gray-600">
              Homebuyer electrical reports across Watford and surrounding areas
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {areasServed.map((area, index) => (
              <span 
                key={index}
                className="px-6 py-3 bg-white rounded-full text-[#0A1628] font-medium border border-gray-200 hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-white transition-all duration-300 cursor-pointer shadow-sm hover:shadow-md"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Buying a Property in Watford?
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Get a professional homebuyer electrical report from NICEIC approved electricians. Know what you're buying before you commit.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a 
              href="tel:02031981818"
              className="bg-[#D4AF37] text-[#0A1628] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#C5A028] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-phone-line"></i>
              Call 020 3198 1818
            </a>
            <Link 
              to="/#contact"
              className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 border border-white/30 whitespace-nowrap"
            >
              Request Callback
            </Link>
          </div>

          <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-300">
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-[#D4AF37] text-xl"></i>
              <span>Fast Reports</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-[#D4AF37] text-xl"></i>
              <span>NICEIC Approved</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-[#D4AF37] text-xl"></i>
              <span>Clear Guidance</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}